import { Bot } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useTestCaseAutomationLinks } from '@/hooks'
import type { TestCaseAutomationLink } from '@/types'

interface TestCaseAutomationTabProps {
  projectId: string
  testCaseId: string
  testCaseIdentifier: string
  isAutomated: boolean
  automationStatus: string
}

export function TestCaseAutomationTab({
  projectId,
  testCaseId,
  testCaseIdentifier,
  isAutomated,
  automationStatus,
}: TestCaseAutomationTabProps) {
  const { data: links, isLoading, isError } = useTestCaseAutomationLinks(projectId, testCaseId)

  if (isLoading) {
    return (
      <div
        className="space-y-3"
        role="status"
        aria-live="polite"
        aria-label="Loading automation links"
        data-testid="automation-links-loading"
      >
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <Card className="p-6 text-center text-sm text-destructive" role="alert" data-testid="automation-links-error">
        <p className="font-medium mb-1">Could not load automation links</p>
        <p>Refresh the page or try again later.</p>
      </Card>
    )
  }

  if (!links || links.length === 0) {
    return (
      <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="automation-links-empty">
        <p className="font-medium text-foreground mb-1">Not automated yet</p>
        <p>
          Run the <span className="font-mono text-xs">Automate Tests with Playwright</span> workflow on the linked work
          item to generate Playwright tests for this test case.
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-4" data-testid="automation-links-list">
      <Card className="p-4 shadow-none">
        <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Status</dt>
          <dd>
            {isAutomated ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-foreground bg-purple px-2.5 py-0.5 text-xs font-medium text-purple-foreground">
                <Bot className="h-3.5 w-3.5" aria-hidden="true" />
                {automationStatus}
              </span>
            ) : (
              <span className="text-muted-foreground">Not automated</span>
            )}
          </dd>
          <dt className="text-muted-foreground">Tag</dt>
          <dd className="font-mono text-foreground">@{testCaseIdentifier}</dd>
          <dt className="text-muted-foreground">Runner</dt>
          <dd>Playwright</dd>
        </dl>
      </Card>

      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
          Linked tests ({links.length})
        </p>
        <Card className="overflow-hidden">
          <table className="w-full table-fixed break-words text-sm" aria-label="Linked Playwright tests">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Test name</th>
                <th className="px-4 py-2 text-left font-medium">Spec file</th>
                <th className="px-4 py-2 text-left font-medium w-[140px]">Linked on</th>
              </tr>
            </thead>
            <tbody>
              {links.map(link => (
                <LinkRow key={link.id} link={link} />
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  )
}

function LinkRow({ link }: Readonly<{ link: TestCaseAutomationLink }>) {
  return (
    <tr className="border-t" data-testid={`automation-link-row-${link.id}`}>
      <td className="px-4 py-3 font-medium">{link.testName}</td>
      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{link.testPath}</td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {new Date(link.linkedOn).toLocaleDateString()}
      </td>
    </tr>
  )
}
